import moment from 'moment'
import React, {FC, useEffect, useState} from 'react'
import DataTable, {TableColumn} from 'react-data-table-component'
import {Spinner} from 'react-bootstrap'
import {useNavigate} from 'react-router-dom'
import useAppToast from 'src/hooks/useAppToast'
import {useAppDispatch, useAppSelector} from 'src/redux/storeHooks'
import CommonAlertModal from 'src/utils/modals/CommonAlertModal'
import {getCustomPlansAsync} from 'src/redux/actions/pricingActions'
import {BillingPlans} from 'src/interfaces/Platform.interface'
import {deleteCustomPlanAsync} from 'src/redux/actions/passwordAction'
import TableLoadingShell from 'src/utils/TableLoadingShell'

const CommonColumn: FC<{rowData: string}> = ({rowData}) => (
  <p className='d-inline-block' style={{maxWidth: '180px'}}>
    {rowData}
  </p>
)

const PlanActions: FC<{row: BillingPlans; onEdit: (row: BillingPlans) => void}> = ({row, onEdit}) => {
  const dispatch = useAppDispatch()
  const {successToast, warningToast} = useAppToast()
  const [isDeleteCustomPlan, setIsDeleteCustomPlan] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  return (
    <div className='d-flex gap-2'>
      <button className='btn btn-sm btn-light-primary' onClick={() => onEdit(row)}>
        Edit
      </button>
      <button className='btn btn-sm btn-danger' onClick={() => setIsDeleteCustomPlan(true)}>
        Delete
      </button>

      <CommonAlertModal
        key={`Delete Custom Plan Confirmation - ${row.id}`}
        title={'Are you sure you want to delete this custom plan?'}
        subtitle=''
        isTermsVisible={false}
        successDisabled={isDeleting}
        successBtnTitle={isDeleting ? 'Deleting...' : 'Delete'}
        cancelBtnTitle={'Cancel'}
        showCloseButton={true}
        modalClassName='custmd'
        isVisible={isDeleteCustomPlan}
        onCancel={() => {
          if (!isDeleting) {
            setIsDeleteCustomPlan(false)
          }
        }}
        onSuccess={() => {
          setIsDeleting(true)
          dispatch(deleteCustomPlanAsync({customPlanDataId: row.id}))
            .unwrap()
            .then((payload: {status_code?: number; massage?: string; statusText?: string}) => {
              const {status_code, massage, statusText} = payload || {}
              if ([200, 201].includes(status_code ?? 0)) {
                successToast(massage || 'Plan deleted successfully.')
                setIsDeleteCustomPlan(false)
              } else {
                warningToast(massage || statusText || 'Plan could not be deleted.')
              }
            })
            .catch((error) => {
              const message =
                error?.data?.massage ||
                error?.data?.message ||
                error?.statusText ||
                'Plan could not be deleted.'
              warningToast(message)
              console.error(error)
            })
            .finally(() => {
              setIsDeleting(false)
            })
        }}
      />
    </div>
  )
}

const PricingPlans: FC = () => {
  const [searchString, setSearchString] = useState('')
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const {customPlanListForUsers, customPlansListLoader, totalCustomPlans} = useAppSelector(
    (state) => state.pricing
  )
  const {fetchedUserAccountData} = useAppSelector((state) => state.userAccountData)
  const isSuperAdminUser =
    fetchedUserAccountData?.user_type === 'SUPERADMIN' ||
    fetchedUserAccountData?.is_superuser ||
    fetchedUserAccountData?.account_type === 'ADMIN_STAFF'
  const managePlanAccessLevel = fetchedUserAccountData?.role?.manage_plan_access?.toUpperCase()
  const canCreateCustomPlan = isSuperAdminUser && managePlanAccessLevel === 'WRITE'

  const [paginationData, setPaginationData] = useState({
    page_limit: 25,
    page_num: 1,
    search: '',
  })

  const handleEditPlan = (plan: BillingPlans) => {
    navigate('/manage-pricing', {state: {mode: 'edit', customPlan: plan}})
  }

  const columns: TableColumn<BillingPlans>[] = [
    {
      name: 'Customer',
      cell: (row) => (row ? <CommonColumn rowData={row?.user?.name ?? ''} /> : null),
      minWidth: '140px',
    },
    {
      name: 'Email',
      selector: (row) => row?.user?.email,
      cell: (row) => (row ? <CommonColumn rowData={row?.user?.email} /> : null),
      minWidth: '200px',
    },
    {
      name: 'Plan',
      cell: (row) => (
        <div>
          <p className='mb-0'>
            $ {row.price.value} / <span>{row?.billing_frequency === 'ANNUAL' ? 'Annually' : 'Monthly'}</span>
          </p>
        </div>
      ),
      minWidth: '100px',
    },
    {
      name: 'Total SKUs ',
      selector: (row) => row?.product_limit,
      cell: (row) => (row ? <CommonColumn rowData={row?.product_limit + ''} /> : null),
      center: true,
      minWidth: '140px',
    },
    {
      name: 'Total Orders',
      selector: (row) => row?.order_limit,
      cell: (row) => (row ? <CommonColumn rowData={row?.order_limit + ''} /> : null),
      center: true,
      minWidth: '150px',
    },
    {
      name: 'Billed Manually',
      selector: (row) => row?.description,
      cell: (row) => (
        row ? <CommonColumn rowData={row?.description === 'BILLED_MANUALLY' ? 'Yes' : 'No'} /> : null
      ),
      center: true,
      minWidth: '140px',
    },
    {
      name: 'Created Date',
      cell: (row) =>
        row.created_at ? moment.utc(row?.created_at).format('hh:mm A, DD-MMM-YYYY') : null,
      sortable: false,
      minWidth: '170px',
    },
    {
      name: 'Updated At',
      cell: (row) =>
        row?.updated_at ? moment.utc(row?.updated_at).format('hh:mm A, DD-MM-YYYY') : null,
      minWidth: '170px',
    },
    ...(canCreateCustomPlan
      ? [
          {
            name: 'Action',
            selector: (row) => row?.id,
            cell: (row) => (row ? <PlanActions row={row} onEdit={handleEditPlan} /> : null),
            center: true,
            minWidth: '190px',
          } as TableColumn<BillingPlans>,
        ]
      : []),
  ]

  useEffect(() => {
    dispatch(getCustomPlansAsync({...paginationData}))
  }, [paginationData, dispatch])

  useEffect(() => {
    if (searchString === '') {
      setPaginationData((prev) => ({
        ...prev,
        search: '',
        page_num: 1,
      }))
    }
  }, [searchString])

  return (
    <div>
      <div className='mb-3'>
        <span className='fw-bold text-lg ' style={{fontSize: '16px'}}>
          Custom Pricing Plans
        </span>
      </div>

      <div className='card border-0 shadow rounded '>
        <div className='card-body' style={{minHeight: 'auto'}}>
          <div className='row align-items-center justify-content-between'>
            <div className='col-md-6 col-sm-12 mb-md-0'>
              <div className='input-group'>
                <span className='input-group-text bg-light'>
                  <i className='bi bi-search text-muted'></i>
                </span>
                <input
                  className='form-control form-control-sm'
                  type='search'
                  placeholder='Search by name or email...'
                  onChange={(ev) => setSearchString(ev.target.value)}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter') {
                      setPaginationData((prev) => ({
                        ...prev,
                        search: searchString,
                        page_num: 1,
                      }))
                    }
                  }}
                  value={searchString}
                />
              </div>
            </div>

            {canCreateCustomPlan && (
              <div className='col-md-6 col-sm-12 text-md-end text-start mt-2 mt-md-0'>
                <button
                  type='button'
                  className='btn btn-sm btn-primary fw-bold d-inline-flex align-items-center gap-2'
                  onClick={() => navigate('/manage-pricing')}
                  title='Add a new custom pricing plan'
                >
                  <i className='bi bi-plus-lg'></i>
                  Add New Plan
                </button>
              </div>
            )}
          </div>
        </div>

        <TableLoadingShell loading={customPlansListLoader}>
        <DataTable
          fixedHeader
          key={paginationData.page_limit + ''}
          data={customPlanListForUsers?.billing_plans}
          columns={columns}
          pagination
          paginationServer
          responsive
          paginationPerPage={paginationData.page_limit}
          paginationTotalRows={totalCustomPlans}
          paginationResetDefaultPage={false}
          progressPending={false}
          onChangePage={(page) => {
            setPaginationData((prev) => ({...prev, page_num: page}))
          }}
          onChangeRowsPerPage={(limit) => {
            setPaginationData((prev) => ({
              ...prev,
              page_limit: limit,
              page_num: 1,
            }))
          }}
          customStyles={{
            table: {
              style: {
                height: '54vh',
              },
            },
            headCells: {
              style: {fontWeight: 'bold', fontSize: '14px'},
            },
          }}
        />
        </TableLoadingShell>
      </div>
    </div>
  )
}

export default PricingPlans
